/**
 * Canonical admin-page header.
 *
 * Replaces the ~9 hand-rolled header blocks in `src/pages/admin/*.jsx` so
 * future admin pages don't drift on:
 *   - back-button target  (must be "/" — there is no `/admin` route)
 *   - typography          (text-base font-black tracking-tighter font-['Chivo'])
 *   - account menu        (right-aligned `AccountMenu`)
 *
 * Variants
 * --------
 *   sticky=false (default)  — flat divider header, scrolls with page.
 *                             Used by 7 of 9 existing admin pages.
 *   sticky=true             — sticky-top + frosted-glass; survives long
 *                             scrolling pages (EmailLimitsPage,
 *                             DriveOauthAdminPage). Use this when the
 *                             page has scroll content > ~1200px.
 *
 * Props
 * -----
 *   icon       — Phosphor (or any) icon node, slotted between back-arrow
 *                and title. Pages typically pass `<Foo weight="fill" />`.
 *   title      — string, the H1 text.
 *   subtitle   — optional string, hidden on `sm:` and below to save space.
 *   user / onLogout / onLogoutAll — pass-through to <AccountMenu/>.
 *   testId     — back-button data-testid (e.g. "email-limits-back"). Each
 *                page should keep its own ID for E2E selector stability.
 *   maxWidth   — Tailwind max-width class for the inner container.
 *                Defaults to `max-w-7xl` (the historical default).
 *   sticky     — opt-in for the frosted-glass sticky variant.
 *   backHref   — override the back-button target (default "/").
 *   children   — optional extra slot rendered between subtitle and the
 *                AccountMenu (rare — used for inline page-level toggles).
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import AccountMenu from "@/components/AccountMenu";
import { APP_VERSION } from "@/lib/version";

export default function AdminPageHeader({
  icon,
  title,
  subtitle,
  user,
  onLogout,
  onLogoutAll,
  testId = "admin-page-back",
  maxWidth = "max-w-7xl",
  sticky = false,
  backHref = "/",
  children,
}) {
  // Sticky variant — frosted glass, stays visible on scroll.
  if (sticky) {
    return (
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className={`${maxWidth} mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-3`}>
          <Link
            to={backHref}
            data-testid={testId}
            className="text-slate-500 hover:text-blue-700 transition shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={18} weight="bold" />
          </Link>
          {icon ? <span className="shrink-0">{icon}</span> : null}
          <h1 className="text-sm sm:text-base font-black tracking-tighter font-['Chivo'] truncate">
            {title}
          </h1>
          <span data-testid="topbar-version"
                title={`App version ${APP_VERSION} — bumped each iteration so you can tell preview from production at a glance.`}
                className="hidden sm:inline text-[9px] font-mono font-bold tracking-wider text-slate-400 cursor-help">
            {APP_VERSION}
          </span>
          {subtitle && (
            <span className="text-xs text-slate-500 hidden sm:inline truncate">
              {subtitle}
            </span>
          )}
          {children}
          <div className="ml-auto shrink-0">
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
      </header>
    );
  }

  // Default variant — flat divider, scrolls with page (matches the
  // majority of existing admin pages: Discount Approvals, Customer Aux,
  // Dealer Agreements, Dealer Scorecard, Automations, Master Doc, etc.)
  return (
    <header
      className={`border-b border-slate-200 px-3 sm:px-4 md:px-8 h-14 flex items-center gap-2 sm:gap-3 ${maxWidth} mx-auto`}
    >
      <Link
        to={backHref}
        data-testid={testId}
        className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500 shrink-0"
        aria-label="Back"
      >
        <ArrowLeft size={18} weight="bold" />
      </Link>
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <h1 className="text-sm sm:text-base font-black tracking-tighter font-['Chivo'] truncate">
        {title}
      </h1>
      {subtitle && (
        <span className="text-xs text-slate-500 hidden sm:inline truncate">
          {subtitle}
        </span>
      )}
      {children}
      <div className="ml-auto shrink-0">
        <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
      </div>
    </header>
  );
}
